import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { saveScroll, readScroll, hasScroll } from '../lib/scrollStore';

/**
 * Ripristino dello scroll fedele alla navigazione:
 *  - PUSH / REPLACE (link nuovo) -> si parte dalla cima;
 *  - POP (tasto indietro/avanti)  -> si torna alla posizione salvata.
 *
 * Sostituisce il vecchio ScrollTop, che azzerava lo scroll a ogni cambio rotta
 * senza distinguere il tipo di navigazione.
 */
export const ScrollRestoration = () => {
  const location = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'POP' | 'REPLACE'
  const chiavePrecedente = useRef(location.key);

  // Il browser tenta un suo ripristino che va in conflitto col nostro: spegnerlo.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const prima = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = prima;
      };
    }
    return undefined;
  }, []);

  // Salva la posizione della rotta che si sta lasciando, prima di cambiarla.
  useEffect(() => {
    const salva = () => saveScroll(chiavePrecedente.current, window.scrollY);
    let inCoda = false;
    const onScroll = () => {
      if (inCoda) return;
      inCoda = true;
      requestAnimationFrame(() => {
        saveScroll(chiavePrecedente.current, window.scrollY);
        inCoda = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      salva();
      window.removeEventListener('scroll', onScroll);
    };
  }, [location.key]);

  // A ogni cambio rotta decide dove posizionarsi.
  useEffect(() => {
    const vaiA = (y) => window.scrollTo(0, y);

    if (navType === 'POP' && hasScroll(location.key)) {
      const y = readScroll(location.key);
      // La pagina di destinazione carica i dati in modo asincrono: al primo
      // frame e' ancora corta e un ripristino verrebbe clampato (e lo
      // scroll-anchoring del browser amplificherebbe l'errore mentre cresce).
      // Si ri-applica il target a ogni frame finche' la pagina non e' abbastanza
      // alta da raggiungerlo, con un tetto di ~1s.
      let tentativi = 0;
      const prova = () => {
        vaiA(y);
        const raggiunto = Math.abs(window.scrollY - y) <= 2;
        if (!raggiunto && tentativi < 60) {
          tentativi += 1;
          requestAnimationFrame(prova);
        }
      };
      requestAnimationFrame(prova);
    } else {
      vaiA(0);
    }

    chiavePrecedente.current = location.key;
  }, [location.key, navType]);

  return null;
};
